import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { type ImportRosterResultDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react'

const TEMPLATE =
  'Company,Company email,Email,First name,Last name\r\n' +
  'Acme Ltd,contact@acme.com,jane.doe@acme.com,Jane,Doe\r\n' +
  'Acme Ltd,contact@acme.com,john.smith@acme.com,John,Smith\r\n' +
  'Globex Ltd,,sara.lee@globex.com,Sara,Lee\r\n'

function downloadTemplate() {
  const blob = new Blob([TEMPLATE], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'certslate-roster-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

interface Props {
  /** Uploads the file to the correct endpoint (tenant-admin or superadmin) and returns the result. */
  upload: (file: File, dryRun: boolean) => Promise<ImportRosterResultDto>
  /** Called after a successful (non-dry-run) import. */
  onImported?: () => void
}

export default function RosterImporter({ upload, onImported }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<ImportRosterResultDto | null>(null)

  const mutation = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => upload(file!, dryRun),
    onSuccess: (data) => {
      setResult(data)
      if (!data.dryRun) onImported?.()
    },
  })

  const busy = mutation.isPending

  return (
    <div className="max-w-2xl bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Import roster</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Bulk-import companies and their students from one CSV. Companies become clients; students become staff.
        </p>
      </div>

      {/* Instructions */}
      <div className="text-sm text-gray-600 space-y-2">
        <p>Required columns (header row required):</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="font-medium">Company</span> — the client organisation</li>
          <li><span className="font-medium">Company email</span> — optional; defaults to the first student's email</li>
          <li><span className="font-medium">Email</span>, <span className="font-medium">First name</span>, <span className="font-medium">Last name</span> — the student</li>
        </ul>
        <p className="text-gray-500">
          Existing companies (matched by name) and students (matched by email) are skipped, so re-running is safe.
        </p>
        <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer">
          <Download size={14} /> Download template
        </button>
      </div>

      {/* File picker */}
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={e => { setFile(e.target.files?.[0] ?? null); setResult(null) }}
        />
        <FileSpreadsheet size={28} className="mx-auto text-gray-300" />
        {file
          ? <p className="mt-2 text-sm text-gray-700 font-medium">{file.name} <span className="text-gray-400">({Math.round(file.size / 1024)} KB)</span></p>
          : <p className="mt-2 text-sm text-gray-500">No file chosen</p>}
        <button onClick={() => fileRef.current?.click()} className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer">
          {file ? 'Choose a different file' : 'Choose CSV file'}
        </button>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" disabled={!file || busy} onClick={() => mutation.mutate({ dryRun: true })}>
          {busy && mutation.variables?.dryRun ? 'Validating…' : 'Validate (dry run)'}
        </Button>
        <Button disabled={!file || busy} onClick={() => mutation.mutate({ dryRun: false })}>
          <Upload size={15} className="mr-1.5" />
          {busy && mutation.variables?.dryRun === false ? 'Importing…' : 'Import'}
        </Button>
        <span className="text-xs text-gray-400">Run a dry run first to preview before importing.</span>
      </div>

      {mutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {(mutation.error as any)?.response?.data?.detail ?? 'Import failed. Check the file and try again.'}
        </p>
      )}

      {/* Result */}
      {result && (
        <div className={`rounded-lg border p-4 ${result.dryRun ? 'border-indigo-200 bg-indigo-50/50' : 'border-green-200 bg-green-50/50'}`}>
          <div className="flex items-center gap-2 mb-3">
            {result.dryRun
              ? <><AlertTriangle size={16} className="text-indigo-600" /><span className="font-semibold text-indigo-900">Dry run — nothing was written</span></>
              : <><CheckCircle2 size={16} className="text-green-600" /><span className="font-semibold text-green-900">Import complete</span></>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Rows processed" value={result.totalRows} />
            <Stat label="Companies created" value={result.companiesCreated} />
            <Stat label="Companies matched" value={result.companiesMatched} />
            <Stat label="Students created" value={result.studentsCreated} />
            <Stat label="Students skipped" value={result.studentsSkipped} />
            <Stat label="Rows with errors" value={result.errorRows} danger={result.errorRows > 0} />
          </div>
          {result.errors.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Errors {result.errors.length < result.errorRows ? `(first ${result.errors.length})` : ''}
              </p>
              <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white divide-y divide-gray-100 text-sm">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-1.5 flex gap-3">
                    <span className="text-gray-400 font-mono shrink-0">Row {e.row}</span>
                    <span className="text-red-600">{e.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.dryRun && result.studentsCreated + result.companiesCreated > 0 && (
            <Button className="mt-4" disabled={busy} onClick={() => mutation.mutate({ dryRun: false })}>
              <Upload size={15} className="mr-1.5" /> Looks good — import now
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div>
      <div className={`text-xl font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}
